import { captureException } from '@sentry/react';
import is from '@sindresorhus/is';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Image, Link as TUILink } from 'theme-ui';
import trackEvent from 'utils/analytics';
import { useUser } from 'v1/hooks/use-user';
import { ArrowRightIcon, Box, Link } from 'v1/ui';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function UpgradeThankYou() {
  const query = useQuery();
  const user = useUser();

  useEffect(() => {
    trackGaPurchase(query);
  }, []); // eslint-disable-line

  // Waiting for auth and resume to load
  if (user === null) {
    return null;
  }

  // User's should never be unauthenticated when viewing this page
  if (user === false) {
    // Log an error to Sentry
    const error = new Error(
      'Unauthenticated users cannot view upgrade thank you page'
    );
    captureException(error, { tags: { noFeedback: true } });

    // Redirect them to the login page
    window.location.pathname = '/login';
    return null;
  }

  return (
    <Box
      sx={{
        width: '100vw',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: [16, 32, 40],
        py: [32, 40, 64],
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: ['column', 'row'],
          alignItems: 'center',
        }}
      >
        <Box sx={{ maxWidth: 450 }}>
          <Box
            sx={{
              fontSize: [32, 40, 48],
              fontWeight: 700,
              lineHeight: 1,
              mb: 30,
            }}
          >
            {user.firstName ? (
              <span>
                Thanks for upgrading,{' '}
                <Box as="span" sx={{ color: 'blue' }}>
                  {user.firstName}
                </Box>
                !
              </span>
            ) : (
              'Thanks for upgrading!'
            )}
          </Box>
          <Box sx={{ color: 'black70' }}>
            <Box sx={{ mb: 3 }}>
              You'll be scheduling interviews in no time!
            </Box>
            <Box sx={{ mb: [32, 40, 50], maxWidth: 270 }}>
              {"If there's anything we can do to help, feel free to "}
              <TUILink
                href="mailto:hello@standardresume.co"
                sx={{
                  color: 'blue',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                }}
              >
                email us
              </TUILink>
              .
            </Box>
          </Box>
          {user.isAnonymous ? (
            <TUILink
              variant="primaryButton"
              href={`/v2/setup/account?fromapp=true`}
              sx={{ width: 150, py: 2, justifyContent: 'space-between' }}
            >
              Continue
              <ArrowRightIcon color="white" />
            </TUILink>
          ) : (
            <Link
              to="resumes"
              variant="primaryButton"
              data-testid="continue-button"
              sx={{ width: 250, py: 2, justifyContent: 'space-between' }}
            >
              Back to my resume
              <ArrowRightIcon color="white" />
            </Link>
          )}
        </Box>
        <Box sx={{ pl: [0, 24, 64], mt: [40, 0] }}>
          <Image
            src="https://assets.standardresume.co/q_auto,f_auto,c_fill,w_1200/v1/app/thank-you/thank-you-card_rlstg9.png"
            sx={{
              width: '100%',
              maxWidth: 400,
              height: 'auto',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// Track purchase event in Google Analytics (Mixpanel tracking done server side)
function trackGaPurchase(query: URLSearchParams) {
  try {
    const amount = query.get('amount');
    if (amount === null)
      throw new Error('Missing required amount query parameter');
    const amountNumber = parseInt(amount, 10);
    if (is.nan(amountNumber)) throw new Error('Amount cannot be NaN');
    trackEvent.gaPurchase(amountNumber);
  } catch (error) {
    // Use $19 to avoid dropping event and log error to Sentry
    trackEvent.gaPurchase(19);
    captureException(error, { tags: { noFeedback: true } });
  }
}
